import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import Icon from '../Icon';
import './Modal.css';

let openModalCount = 0;

/**
 * Accessible modal dialog rendered through a portal.
 * - closes on Escape and backdrop click (configurable)
 * - locks body scroll while open
 * - responsive: becomes near-fullscreen on small viewports (see CSS)
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md', // sm | md | lg
  closeOnBackdrop = true,
  className,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    openModalCount++;
    document.body.style.overflow = 'hidden';
    return () => {
      openModalCount--;
      if (openModalCount <= 0) {
        openModalCount = 0;
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="kvo-modal__backdrop" onMouseDown={closeOnBackdrop ? onClose : undefined}>
      <div
        className={cn('kvo-modal', `kvo-modal--${size}`, className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="kvo-modal__header">
          <h3 className="kvo-modal__title">{title}</h3>
          <button type="button" className="kvo-modal__close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>
        <div className="kvo-modal__body">{children}</div>
        {footer && <footer className="kvo-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
